import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { ResidentCarFindInterface } from '@zaoblako/shared/modules/cctv/dtos/resident-car/resident-car-find.dto';
import {
  ResidentCarFormDto,
  ResidentCarFormInterface
} from '@zaoblako/shared/modules/cctv/dtos/resident-car/resident-car-form.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { ResidentCarService } from '@zaoblako/web/core/services/passage/resident-car.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ClientAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/client-auto-complete/client-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { CarCategorySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/car-category-single-select/car-category-single-select.component';
import { ExternalUserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/client/external-user-single-select/external-user-single-select.component';
import { RoomMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/room-multi-select/room-multi-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'crm-resident-car-form',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    SpinnerComponent,
    ButtonModule,
    JoinPipe,
    InputTextComponent,
    DividerModule,
    CarCategorySingleSelectComponent,
    ClientAutoCompleteComponent,
    RoomMultiSelectComponent
  ],
  templateUrl: './resident-car-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResidentCarFormComponent extends BaseFormComponent<ResidentCarFormInterface> implements OnInit, OnDestroy {
  @Input() public readonly index: number;
  public car = <ResidentCarFormInterface>{};
  public dto = ResidentCarFormDto;

  @ViewChild('externalUserSingleSelectComponent')
  public readonly externalUserSingleSelectComponent: ExternalUserSingleSelectComponent;

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;
  public engine: 'class-transformer' | 'serde' = 'serde';

  private formSubscription$: Subscription = new Subscription();

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly residentCarService: ResidentCarService,
    private readonly errorService: ErrorService,
    private readonly router: Router,
    private readonly confirmService: ConfirmDialogService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public ngOnDestroy(): void {
    this.formSubscription$?.unsubscribe();
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.residentCarService
      .findById<ResidentCarFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (car) => {
          this.car = car;

          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveCar() {
    if (this.checkValidate(this.car).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.residentCarService.update<ResidentCarFormInterface, ResidentCarFindInterface>(this.car).subscribe({
          next: (car) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`ТС "${car?.carNumber}" обновлено`);
            this.formStoreService.saveNext(car, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.residentCarService.create<ResidentCarFindInterface>(this.car).subscribe({
          next: (car) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('ТС создано');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router.navigate(['/passage/resident-car/card/' + car.id]).catch(console.error);
            } else {
              this.formStoreService.saveNext(car, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteCar() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить ТС "${this.car?.carNumber}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.residentCarService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe(() => {
          this.errorService.addSuccessMessage(`ТС "${this.car?.carNumber}" удалено`);
          this.formStoreService.deleteNext(this.index);
        });
      }
    });
  }

  public restoreCar() {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить постоянный ТС "${this.car?.carNumber}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.residentCarService.restore(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.car = { ...this.car, deleted: null } as ResidentCarFindInterface;
            this.cdRef.markForCheck();
            this.errorService.addSuccessMessage(`Постоянный ТС "${this.car?.carNumber}" восстановлен`);
            this.formStoreService.saveNext(this.car, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }
}
